/**
 * SlotSelector Component
 * Visual time slot selection grid for astrologers
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  toggleSlot,
  selectAllPeriod,
  clearSelection,
  clearPeriodSelection
} from '../../store/slices/availabilitySlice';
import { calculateSlotTime, getPeriodLabel, getAllPeriodIndices } from '../../utils/slotUtils';

const SlotSelector = () => {
  const dispatch = useDispatch();
  const { selectedSlots } = useSelector(state => state.availability);

  const periods = {
    morning: { color: '#E8F5E9', selectedColor: '#4CAF50' },
    noon: { color: '#FFF3E0', selectedColor: '#FF9800' },
    evening: { color: '#E3F2FD', selectedColor: '#2196F3' }
  };

  const isSlotSelected = (period, index) => {
    const slotGroup = selectedSlots.find(s => s.period === period);
    return slotGroup?.slot_indices.includes(index) || false;
  };

  const handleSlotClick = (period, index) => {
    dispatch(toggleSlot({ period, index }));
  };

  const handleSelectAllPeriod = (period) => {
    const indices = getAllPeriodIndices(period);
    dispatch(selectAllPeriod({ period, indices }));
  };

  const handleClearPeriod = (period) => {
    dispatch(clearPeriodSelection(period));
  };

  const handleClearAll = () => {
    dispatch(clearSelection());
  };

  const getTotalSelected = () => {
    return selectedSlots.reduce((sum, group) => sum + group.slot_indices.length, 0);
  };

  const getPeriodSelectedCount = (period) => {
    const slotGroup = selectedSlots.find(s => s.period === period);
    return slotGroup?.slot_indices.length || 0;
  };

  const renderPeriodSlots = (period) => {
    const indices = getAllPeriodIndices(period);
    const { color, selectedColor } = periods[period];
    const slots = [];

    indices.forEach(i => {
      const { start, end } = calculateSlotTime(i, period);
      const selected = isSlotSelected(period, i);

      slots.push(
        <Grid item xs={12} sm={6} md={4} lg={3} key={`${period}-${i}`}>
          <Card
            onClick={() => handleSlotClick(period, i)}
            sx={{
              cursor: 'pointer',
              backgroundColor: selected ? selectedColor : color,
              border: selected ? `2px solid ${selectedColor}` : '1px solid #e0e0e0',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 2,
                backgroundColor: selected ? selectedColor : '#f5f5f5',
              },
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" fontWeight={selected ? 'bold' : 'normal'}>
                {start} - {end}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Slot {i}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      );
    });

    return slots;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h6">
            Select Time Slots
          </Typography>
          <Chip
            label={`${getTotalSelected()} slot${getTotalSelected() !== 1 ? 's' : ''} selected`}
            color="primary"
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>
        <Button
          variant="outlined"
          color="error"
          onClick={handleClearAll}
          disabled={getTotalSelected() === 0}
        >
          Clear All
        </Button>
      </Box>

      {Object.keys(periods).map(period => (
        <Box key={period} sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Box>
              <Typography variant="h6">
                {getPeriodLabel(period)}
              </Typography>
              {getPeriodSelectedCount(period) > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {getPeriodSelectedCount(period)} selected
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleSelectAllPeriod(period)}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => handleClearPeriod(period)}
                disabled={getPeriodSelectedCount(period) === 0}
              >
                Clear
              </Button>
            </Box>
          </Box>
          <Grid container spacing={1}>
            {renderPeriodSlots(period)}
          </Grid>
          <Divider sx={{ mt: 3 }} />
        </Box>
      ))}
    </Box>
  );
};

export default SlotSelector;
